import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import CurveChart from './CurveChart';
import { startRound, submitRound, getGuestToken, clearGuestToken, claimGuestDraws } from './curveHistoricalApi';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/* ════════ Keep playing — draw a real player's history from memory ════════
   Lives inside CurveToday, right after today's line is locked. Same
   mechanic as The Curve (draw a value line, get scored), pointed backward:
   you're told the player and the real date range, given the value it
   started at, and draw where you think it went. No daily cap — "Go again"
   keeps pulling fresh players. Reuses CurveToday's cv-* classes/CSS custom
   properties (this only ever mounts inside .cv-page), adding a handful of
   cv-hist-* rules of its own below. */

function fmtMonYear(dateStr) {
  if (!dateStr) return '';
  return new Date(`${dateStr}T12:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    .toUpperCase();
}

function fmtV(v) {
  if (v == null) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);
}

function addDaysStr(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function verdictFor(fit) {
  if (fit >= 90) return "That's about as close as it gets.";
  if (fit >= 75) return 'A strong read on him.';
  if (fit >= 55) return 'Real signal, real misses.';
  if (fit >= 35) return 'The shape mostly got away from you.';
  return 'Not your best rewind.';
}

const styles = `
  .cv-hist { margin-top: 28px; padding-top: 24px; border-top: 1px solid var(--line); }
  .cv-hist-eyebrow { font-family: var(--mono); font-size: 10.5px; font-weight: 700; letter-spacing: .2em;
    color: var(--brand-ink); margin-bottom: 6px; }
  .cv-hist-title { font-size: 20px; font-weight: 700; letter-spacing: -.02em; color: var(--ink); margin-bottom: 4px; }
  .cv-hist-sub { font-size: 14px; color: var(--ink2); line-height: 1.5; margin-bottom: 16px; }
  .cv-hist-result { background: var(--paper); border: 1px solid var(--paper-edge); border-radius: 16px;
    padding: 22px 20px; margin-bottom: 14px; text-align: center; }
  .cv-hist-fit { font-size: 52px; font-weight: 700; letter-spacing: -.03em; line-height: 1; color: var(--ink); }
  .cv-hist-fit-k { font-family: var(--mono); font-size: 9.5px; font-weight: 700; letter-spacing: .16em;
    color: var(--ink3); margin-top: 6px; }
  .cv-hist-verdict { font-size: 15.5px; color: var(--ink2); margin-top: 10px; }
  .cv-hist-err { text-align: center; font-size: 13px; color: #FF3B30; margin-top: 10px; }
  .cv-hist-rounds { text-align: center; font-family: var(--mono); font-size: 10.5px; color: var(--ink3);
    letter-spacing: .1em; margin-top: 10px; }
  @media (max-width: 600px) {
    .cv-hist-fit { font-size: 44px; }
  }
`;

export default function CurveHistorical() {
  const { user } = useAuth();
  const [phase, setPhase] = useState('idle'); // idle | loading | play | result
  const [line, setLine] = useState(null);
  const [myDraw, setMyDraw] = useState(null);
  const [othersCount, setOthersCount] = useState(0);
  const [drawn, setDrawn] = useState(null);
  const [locking, setLocking] = useState(false);
  const [error, setError] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [roundsPlayed, setRoundsPlayed] = useState(0);
  const [isMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  const claimedRef = useRef(false);

  // Signed in with leftover guest draws (from playing before logging in)?
  // Claim them into the account once.
  useEffect(() => {
    if (!user || claimedRef.current) return;
    const guest = getGuestToken();
    if (!guest) return;
    claimedRef.current = true;
    claimGuestDraws(guest).then(() => clearGuestToken()).catch(() => {});
  }, [user]);

  const handleStart = async () => {
    setPhase('loading');
    setError(null);
    try {
      const result = await startRound();
      if (!result.line) {
        setError("Out of fresh players for now — you've played today's whole pool. Check back tomorrow.");
        setPhase('idle');
        return;
      }
      setLine(result.line);
      setMyDraw(null);
      setOthersCount(result.othersCount || 0);
      setDrawn(null);
      setChartKey((k) => k + 1);
      setPhase('play');
    } catch (e) {
      setError(e.message);
      setPhase('idle');
    }
  };

  const handleLock = async () => {
    if (!drawn || locking) return;
    setLocking(true);
    setError(null);
    try {
      const result = await submitRound(line.id, drawn);
      if (navigator.vibrate) navigator.vibrate(20);
      setLine(result.line);
      setMyDraw(result.myDraw);
      setOthersCount(result.othersCount || 0);
      setDrawn(null);
      setRoundsPlayed((n) => n + 1);
      setPhase('result');
    } catch (e) {
      setError(e.message);
    } finally {
      setLocking(false);
    }
  };

  if (phase === 'idle') {
    return (
      <>
        <style>{styles}</style>
        <div className="cv-hist">
          <div className="cv-hist-eyebrow">KEEP PLAYING</div>
          <div className="cv-hist-title">Draw someone from memory.</div>
          <div className="cv-hist-sub">Same game, pointed backward — a real player, a real 2-3 year stretch. No daily limit.</div>
          <button className="cv-lock" onClick={handleStart}>Play a historical round</button>
          {error ? <div className="cv-hist-err">{error}</div> : null}
        </div>
      </>
    );
  }

  if (phase === 'loading' || !line) {
    return (
      <>
        <style>{styles}</style>
        <div className="cv-hist">
          <div className="cv-hist-eyebrow">KEEP PLAYING</div>
          <div className="cv-note">Pulling someone from the archive…</div>
        </div>
      </>
    );
  }

  const locked = phase === 'result' && !!myDraw;
  const weekN = line.weekCount;
  // Rewind's windows run 2-3 years — a single midpoint tick (plus start/end)
  // reads sparse on desktop, where there's room for quarter marks too.
  const dateTicks = [];
  if (line.startDate && weekN >= 12) {
    const weeksAt = (frac) => Math.floor(weekN * frac);
    const marks = !isMobile && weekN >= 24 ? [0.25, 0.5, 0.75] : [0.5];
    for (const frac of marks) {
      const week = weeksAt(frac);
      dateTicks.push({ kind: 'week', week, label: fmtMonYear(addDaysStr(line.startDate, week * 7)) });
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="cv-hist">
        <div className="cv-hist-eyebrow">KEEP PLAYING · ROUND {roundsPlayed + (locked ? 0 : 1)}</div>

        <div className="cv-player">
          <div className="cv-ava">
            {line.player.sleeperId ? (
              <img src={`${SLEEPERCDN}${line.player.sleeperId}.jpg`} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
            {(line.player.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="cv-name">{line.player.name}</div>
            <div className="cv-meta">
              {[line.player.position, line.player.team].filter(Boolean).join(' · ')} · {fmtMonYear(line.startDate)} → {fmtMonYear(line.endDate)}
              {line.player.ktcUrl ? (
                <> · <a href={line.player.ktcUrl} target="_blank" rel="noopener noreferrer">View on KeepTradeCut →</a></>
              ) : null}
            </div>
          </div>
        </div>

        <p className="cv-ask">
          {locked
            ? <>Here's what really happened. <b>{verdictFor(myDraw.fit)}</b></>
            : <>He started at <b>{fmtV(line.startValue)}</b> in <b>{fmtMonYear(line.startDate)}</b>. Draw his <b>KTC value</b> from there, week by week, to <b>{fmtMonYear(line.endDate)}</b>.</>}
        </p>

        <div className="cv-card">
          <CurveChart
            key={chartKey}
            history={[]}
            historySplit={0.03}
            startValue={line.startValue}
            valueBounds={line.valueMin != null && line.valueMax != null ? { min: line.valueMin, max: line.valueMax } : null}
            horizonWeeks={weekN}
            marks={locked ? line.values : []}
            myPoints={locked ? myDraw.points : null}
            interactive={!locked}
            onDrawChange={setDrawn}
            tall={isMobile}
            todayLabel=""
            startLabel={fmtMonYear(line.startDate)}
            endLabel={fmtMonYear(line.endDate)}
            drawHint={['touch the dot,', 'draw what happened →']}
            ticks={dateTicks}
          />
          <div className="cv-legend">
            <span className="cv-lg y"><i></i> YOUR LINE</span>
            {locked ? <span className="cv-lg r"><i></i> WHAT REALLY HAPPENED</span> : null}
          </div>
        </div>

        {!locked ? (
          <>
            <div className="cv-actions">
              <button className="cv-lock" disabled={!drawn || locking} onClick={handleLock}>
                {locking ? 'Locking…' : drawn ? 'Lock it in' : 'Draw his line to unlock'}
              </button>
              {drawn ? (
                <button className="cv-redraw" onClick={() => { setDrawn(null); setChartKey((k) => k + 1); }}>
                  ↺ Scrap it, draw again
                </button>
              ) : null}
            </div>
            <div className="cv-note">Locking is final — then you see what really happened.</div>
            {error ? <div className="cv-hist-err">{error}</div> : null}
          </>
        ) : (
          <>
            <div className="cv-hist-result">
              <div className="cv-hist-fit">{Math.round(myDraw.fit)}</div>
              <div className="cv-hist-fit-k">FIT SCORE</div>
              <div className="cv-hist-verdict">{verdictFor(myDraw.fit)}</div>
              <div className="cv-stats">
                <div className="cv-stat">
                  <div className="n">{myDraw.mape != null ? `${myDraw.mape}%` : '—'}</div>
                  <div className="k">AVG WEEKLY MISS</div>
                </div>
                <div className="cv-stat">
                  <div className="n">{myDraw.percentile != null ? `${Math.round(myDraw.percentile)}%` : '—'}</div>
                  <div className="k">BETTER THAN</div>
                </div>
              </div>
            </div>
            <button className="cv-lock" onClick={handleStart}>Go again — new player</button>
            <div className="cv-hist-rounds">
              {roundsPlayed} ROUND{roundsPlayed === 1 ? '' : 'S'} TODAY
              {othersCount > 1 ? ` · ${othersCount - 1} OTHER${othersCount === 2 ? '' : 'S'} PLAYED THIS ONE` : ''}
            </div>
            {error ? <div className="cv-hist-err">{error}</div> : null}
          </>
        )}
      </div>
    </>
  );
}
